import { FormLocalizationProvider } from '@faceup/form'
import { defineMessages, useIntl } from '@faceup/localization'
import {
  EnvironmentMessage,
  UiProvider as FUUiProvider,
  type PagesTranslationItems,
  type PagesTranslationVariants,
  type UiProviderProps,
} from '@faceup/ui'
import { UiBaseProvider } from '@faceup/ui-base'
import type { Institution, Plan } from '@faceup/utils'
import { type ReactNode, useContext, useState } from 'react'
import { sharedMessages } from '../Shared/translations'
import { LanguageContext } from './LanguageContext'

const messages = defineMessages('followUp.appProviders', [
  { id: 'confirmRedirectMessageTitle', defaultMessage: 'Leaving FaceUp' },
  {
    id: 'confirmRedirectMessageContent',
    defaultMessage: 'Are you sure you want to leave this page and follow the link?',
  },
  { id: 'redirectOkButton', defaultMessage: 'Follow link' },
  { id: 'redirectCancelButton', defaultMessage: 'Go back' },
  { id: 'ok', defaultMessage: 'OK' },
  { id: 'cancel', defaultMessage: 'Cancel' },
  {
    id: 'unsavedChangesCount',
    defaultMessage: 'You have {count} unsaved changes',
    values: {
      count: true,
    },
  },
  {
    id: 'leaveQuestion',
    defaultMessage: 'You are about to leave a page on which you have unsaved changes.',
  },
  { id: 'leaveTitle', defaultMessage: 'Save before leaving?' },
  { id: 'uploadInputText', defaultMessage: 'Attachments' },
] as const)

const messagesPages = {
  freeSchool: defineMessages('followUp.appProviders.pages.freeSchool', [
    { id: 'title', defaultMessage: 'Homepage' },
    {
      id: 'content',
      defaultMessage:
        '<h2>Online trust box – {name}</h2><p>Is anything happening at school that you don’t like? Have you witnessed or been a victim of bullying? Do you want to say something or ask anything, but are embarrassed? Let us know through the <strong>FaceUp online trust box</strong>.</p><p>This trust box is safe and anonymous, so you don’t have to be afraid of anything turning against you. All cases will be received by a designated person at our school. If you want, you can opt out of anonymity at any time, but you can also stay anonymous.</p><p><strong>How to properly send a case?</strong></p><ul><li><p>Always write only truthful information</p></li><li><p>Describe the situation and the people involved in detail</p></li></ul>',
    },
  ] as const),
  unregisteredSchool: defineMessages('followUp.appProviders.pages.unregisteredSchool', [
    { id: 'title', defaultMessage: 'Homepage' },
    {
      id: 'content',
      defaultMessage:
        '<h2>Online trust box – {name}</h2><p>Do you have anything at your school that you don’t like? Have you been a witness or victim of bullying? Do you want to say or ask anything but feel ashamed? Let us know through the <strong>FaceUp online trust box</strong>.</p><p><strong>Your school does not yet use FaceUp.</strong></p><p>Still, we will do everything to make sure your case <strong>gets to the right person</strong> who can handle it. This is usually a prevention specialist, school psychologist, or educational counselor.</p><p><strong>How does FaceUp work?</strong></p><p>This trust box is safe and anonymous. No one will find out who sent the case. All cases sent through FaceUp will reach the designated person at the school.</p><p><strong>How to send a case correctly?</strong></p><ul><li><p>Always write only truthful information</p></li><li><p>Describe the situation and the people involved in detail.</p></li></ul>',
    },
  ] as const),
  testingSchool: defineMessages('followUp.appProviders.pages.testingSchool', [
    { id: 'title', defaultMessage: 'Homepage' },
    {
      id: 'content',
      defaultMessage:
        '<h2>Test online trust box</h2>\n<p><strong>This trust box is not real. However, you can use it to send a test case and see how it works.</strong></p>\n\n<p>Anyone who is a witness or victim of bullying, inappropriate behavior or has a problem they are ashamed to talk about personally can reach out through the FaceUp online trust box. The cases are anonymous, so students do not have to worry about the case being used against them.</p>\n\n<p>If you want to submit a <strong>real case of bullying</strong>, look for your school and send the case to that school. In case of a life-threatening situation, call 911.</p>',
    },
  ] as const),
  school: defineMessages('followUp.appProviders.pages.school', [
    { id: 'title', defaultMessage: 'Homepage' },
    {
      id: 'content',
      defaultMessage:
        '<h2>Online trust box – {name}</h2><p>Is there anything happening at school that you don’t like? Have you witnessed or been a victim of bullying? Do you want to say or ask something, but are ashamed? Let us know through the <strong>FaceUp online trust box</strong>.</p><p>This trust box is safe and anonymous, so you don’t have to worry about anything turning against you. All cases will be sent to a designated person at our school. If you want to, you can leave anonymity at any time, but you can stay in it.</p><p><strong>How to send a case correctly?</strong></p><ul><li><p>Always write only truthful information</p></li><li><p>Describe the situation and the persons involved in detail</p></li><li><p>If necessary, attach attachments (e.g. photo)</p></li></ul><p><strong>Stay in touch</strong></p><p>Once you have sent the case, safely save the key that appears after sending the case. With this key, you can log in later and see the response. Probably, through the trust box, we will ask you for more information.</p>',
    },
  ] as const),
  company: defineMessages('followUp.appProviders.pages.company', [
    { id: 'title', defaultMessage: 'Homepage' },
    {
      id: 'content',
      defaultMessage:
        '<h2>Whistleblowing Channel – {name}</h2><p>Do you suspect unethical behavior in our organization or want to send us any feedback? Use our whistleblowing channel.</p><p>Our company values ethical behavior and mutual respect. In case you witness unethical behavior that is difficult to discuss personally, we have established a whistleblowing channel. Through this channel, you can safely send cases of illegal or unethical behavior, improvement suggestions, questions, or any feedback.</p><p>All cases are handled by designated persons in our organization. Information sent through the whistleblowing channel is completely secure and there is no risk of retaliation.</p><p><strong>How to send a case correctly?</strong></p><ul><li><p>Always send true information</p></li><li><p>Be as specific as possible - describe the situation and individuals involved in detail</p></li></ul><p><strong>Stay in touch</strong></p><p>We would be happy if you stay in touch with us. We may ask for more information and also inform you of the resolution of your case. So, safely save the key to your case, which will be displayed after sending the case. You can return to the case at any time with this key.</p>',
    },
  ] as const),
  surveyBeforeSend: defineMessages('followUp.appProviders.pages.surveyBeforeSend', [
    { id: 'title', defaultMessage: 'Survey to help us improve' },
    {
      id: 'content',
      defaultMessage:
        'Hello. This survey is designed to help us understand your needs and expectations better. Completing the form will only take a few minutes and your answers will remain completely anonymous.',
    },
  ] as const),
  surveyAfterSend: defineMessages('followUp.appProviders.pages.surveyAfterSend', [
    { id: 'title', defaultMessage: 'Thank you' },
    {
      id: 'content',
      defaultMessage: 'Thank you for your willingness to share your thoughts with us.',
    },
  ] as const),
} satisfies Record<
  Exclude<PagesTranslationVariants, 'formItems'>,
  Record<PagesTranslationItems, unknown>
>

type UiProviderProviderProps = {
  children: ReactNode
  /** resolved primary color (reportFormColor -> institution -> partner) */
  primaryColor?: string
  institutionName: string
  mother: {
    type: Institution | null
    isTesting: boolean | null
    plan: Plan | null
  }
}

/**
 * Top-down theming and UI localization, reused by the report and survey branches.
 * Colors and branding come straight from the init query (resolved by the branch
 * wrapper).
 */
export const UiProvider = ({
  children,
  primaryColor,
  institutionName: name,
  mother,
}: UiProviderProviderProps) => {
  const { formatMessage } = useIntl()
  const { antLocale, direction } = useContext(LanguageContext)
  const [isDefaultForm, setIsDefaultForm] = useState(false)

  const translations: NonNullable<UiProviderProps['pages']>['translations'] = {
    company: {
      title: formatMessage(messagesPages.company.title),
      content: formatMessage(messagesPages.company.content).split('{name}').join(name),
    },
    school: {
      title: formatMessage(messagesPages.school.title),
      content: formatMessage(messagesPages.school.content).split('{name}').join(name),
    },
    testingSchool: {
      title: formatMessage(messagesPages.testingSchool.title),
      content: formatMessage(messagesPages.testingSchool.content).split('{name}').join(name),
    },
    freeSchool: {
      title: formatMessage(messagesPages.freeSchool.title),
      content: formatMessage(messagesPages.freeSchool.content).split('{name}').join(name),
    },
    unregisteredSchool: {
      title: formatMessage(messagesPages.unregisteredSchool.title),
      content: formatMessage(messagesPages.unregisteredSchool.content).split('{name}').join(name),
    },
    surveyBeforeSend: {
      title: formatMessage(messagesPages.surveyBeforeSend.title),
      content: formatMessage(messagesPages.surveyBeforeSend.content),
    },
    surveyAfterSend: {
      title: formatMessage(messagesPages.surveyAfterSend.title),
      content: formatMessage(messagesPages.surveyAfterSend.content),
    },
    formItems: {
      title: '',
      content: '',
    },
  }

  return (
    <UiBaseProvider colorPrimary={primaryColor} locale={antLocale} direction={direction}>
      <FUUiProvider
        linkExternalConfirm={{
          confirmRedirectMessageTitle: formatMessage(messages.confirmRedirectMessageTitle),
          confirmRedirectMessageContent: formatMessage(messages.confirmRedirectMessageContent),
          redirectOkButton: formatMessage(messages.redirectOkButton),
          redirectCancelButton: formatMessage(messages.redirectCancelButton),
        }}
        pages={{
          mother,
          isDefaultForm,
          setIsDefaultForm,
          translations,
        }}
        buttonConfirm={{
          confirm: formatMessage(messages.ok),
          cancel: formatMessage(messages.cancel),
        }}
        attachmentList={{
          download: formatMessage(sharedMessages.download),
        }}
      >
        <FormLocalizationProvider
          submitButton={{
            send: formatMessage(sharedMessages.send),
            add: formatMessage(sharedMessages.add),
            save: formatMessage(sharedMessages.save),
          }}
          successMessage={{
            send: formatMessage(sharedMessages.savedMessage),
            add: formatMessage(sharedMessages.savedMessage),
            save: formatMessage(sharedMessages.savedMessage),
          }}
          modalForm={{
            cancelButton: formatMessage(sharedMessages.cancel),
          }}
          uploadInput={{
            text: formatMessage(messages.uploadInputText),
          }}
          unsavedChanges={count =>
            formatMessage(messages.unsavedChangesCount, { values: { count } })
          }
          leaveUnsavedChanges={formatMessage(messages.leaveQuestion)}
          leaveUnsavedChangesTitle={formatMessage(messages.leaveTitle)}
        >
          {children}
          {['stage', 'preview'].includes(import.meta.env.VITE_ENVIRONMENT ?? '') && (
            <EnvironmentMessage environment={import.meta.env.VITE_ENVIRONMENT ?? ''} />
          )}
        </FormLocalizationProvider>
      </FUUiProvider>
    </UiBaseProvider>
  )
}
